/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// UI lib dependencies
import {
	ActionIcon,
	Box,
	Flex,
	Skeleton,
	Image,
	Text,
} from '@mantine/core';

// Styles
import classes from './CompensationSection.module.css';

import HeaderSection from '../HeaderSection/HeaderSection';
import { TOffset } from '@features/offsets/types';
import { IconDownload } from '@tabler/icons-react';
import { MintStatus } from '@components/atoms';
import {
	capitalizeString,
	getMintStatusLabel,
	getMintStatusType,
} from '@utils/functions';
import { useTranslation } from 'react-i18next';
import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';
import { ROLES } from '@config/constants/roles';
import { useAuthorization } from '@lib/authorization';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const CompensationSection = (props: {
	isLoading: boolean;
	data: TOffset[] | [];
	onSeeAll: () => void;
	onDownload: (data: TOffset) => void;
}) => {
	const {
		isLoading,
		onSeeAll,
		data: offsets,
		onDownload,
	} = props;
	/* ********************************** HOOKS ********************************* */

	const { t } = useTranslation();
	const { checkRole } = useAuthorization();

	if (isLoading)
		return (
			<>
				{[...Array(5)].map((_, rowIndex) => (
					<Flex
						key={rowIndex}
						className={classes.containerSkeletons}
						gap="md"
						style={{ width: '100%', marginBottom: 8 }}
					>
						{[...Array(8)].map((_, colIndex) => (
							<Skeleton
								key={colIndex}
								height={40}
								width="80%"
								radius="md"
							/>
						))}
					</Flex>
				))}
			</>
		);

	return (
		<Box className={classes.container}>
			<HeaderSection
				title={t('global.lastOffsets')}
				onSeeAll={onSeeAll}
			/>
			{offsets && offsets.length > 0 && (
				<table className={classes.tableStyle}>
					<thead>
						<tr>
							<th className={classes.thStyle}>
								{t('global.brand')}
							</th>
							<th className={classes.thStyle}>
								{t('global.name')}
							</th>
							<th className={classes.thStyle}>
								{t('global.contributionDate')}
							</th>
							<th className={classes.thStyle}>
								{t('global.quantity')}
							</th>
							<th className={classes.thStyle}>
								{t('global.totalPrice') +
									' ' +
									' (' +
									t('global.exclVat') +
									')'}
							</th>
							<th className={classes.thStyle}>
								{t('global.status')}
							</th>
							{checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							]) && (
								<th
									className={classes.thStyleCertificate}
								>
									{t('global.certificate')}
								</th>
							)}
						</tr>
					</thead>
					<tbody>
						{offsets.map((row: any, index) => (
							<tr key={index}>
								<td className={classes.tdStyle}>
									{row.brand}
								</td>
								<td className={classes.tdStyle}>
									{row.name}
								</td>
								<td className={classes.tdStyle}>
									{row.contributionDate}
								</td>
								<td className={classes.tdStyle}>
									{row.quantity}
								</td>
								<td className={classes.tdStyle}>
									{row.priceTTC}
								</td>
								<td className={classes.tdStyle}>
									<MintStatus
										type={getMintStatusType(
											row && row.status
										)}
									>
										{capitalizeString(
											getMintStatusLabel(
												row && row.status.toUpperCase()
											)
										)}
									</MintStatus>
								</td>
								{checkRole([
									ROLES.ADMIN,
									ROLES.SUPERADMIN,
									ROLES.STAFF,
									ROLES.ORGANIZATION_ADMIN,
									ROLES.ORGANIZATION_STAFF,
									ROLES.BRAND_ADMIN,
									ROLES.BRAND_STAFF,
								]) && (
									<td
										className={classes.tdStyleCertificate}
									>
										{row.status == 'SUCCESS' && (
											<ActionIcon
												onClick={() => {
													onDownload(row);
												}}
												radius={'7px'}
												variant="light"
												className={classes.actionIcon}
												aria-label="Settings"
											>
												<IconDownload
													size={'16px'}
													stroke={1.67}
												/>
											</ActionIcon>
										)}
									</td>
								)}
							</tr>
						))}
					</tbody>
				</table>
			)}
			{offsets.length == 0 && (
				<Box className={classes.containerTextsNoRecords}>
					<Image
						src={NoRecordsIcon}
						className={classes.iconNoRecords}
					/>
					<Text className={classes.titleNoRecords}>
						{t('global.datatable.noResultsFound')}
					</Text>
				</Box>
			)}
		</Box>
	);
};
